import React, { useContext, useMemo, useCallback } from 'react'
import { Link } from 'rebass'

import { useTranslation } from 'i18n'
import SBox from './opinionless/SBox'
import SText from './opinionless/SText'
import { useSTheme } from '../styles/theme'

import { useAuth } from 'v2/hooks/useAuth'
import { UserContext } from 'v2/context/user'
import SIcon from './opinionless/SIcon'

import WebsiteSvg from '@svgs/social-web.svg'
import FacebookSvg from '@svgs/social-facebook.svg'
import InstagramSvg from '@svgs/social-instagram.svg'
import TwitterSvg from '@svgs/social-twitter.svg'
import { Container, PolicyEngineLogo, SocialIcons, StyledF4GILogo, StyledPatternImage } from './styles'
import { SPageType } from './opinionless/SPage'
import ConditionalRender from 'utils/conditionalRender'

interface Props {
    pageType: SPageType
}

const BrandedSideBarLegacy: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const websiteLink = window.ORGANIZATION_CONFIGS?.websiteLink
    const instagramLink = window.ORGANIZATION_CONFIGS?.instagramLink
    const twitterLink = window.ORGANIZATION_CONFIGS?.twitterLink
    const facebookLink = window.ORGANIZATION_CONFIGS?.facebookLink
    const isSideSmallLogo = useMemo(() => {
        return (
            window.PROGRAM.name.toLowerCase().includes('sonoma') ||
            window.PROGRAM.name.toLowerCase().includes('pathway') ||
            (window.PROGRAM.name.toLowerCase().includes('chicago') && window.PROGRAM.name.toLowerCase().includes('ftx'))
        )
    }, [window])

    const { authenticatedUser } = useContext(UserContext)
    const { logoutUser } = useAuth()

    const onClickSignOut = useCallback(
        (event) => {
            event.preventDefault()
            void logoutUser()
        },
        [logoutUser]
    )

    return (
        <Container
            sx={{
                width: '100%',
                height: '100%',
            }}
        >
            <ConditionalRender if={window.innerWidth >= 900}>
                <StyledPatternImage
                    sx={{ minHeight: '100%', height: 'auto' }}
                    src={theme.images.sideBarImage}
                    alt={'pattern image'}
                />

                {/*TODO (Alex) REVERT BY PRODUCTION DEPLOY LINE BELOW*/}
                <StyledF4GILogo padding={20} src={theme.logos.mainLogo} width="100%" height="auto" alt={'logo'} />
                {props.pageType === 'Wizard' ? (
                    <PolicyEngineLogo padding={24} src={theme.logos.policyEngineLogo} alt={'policy engine'} />
                ) : null}
                <SBox flex={1} />
                <SBox flex={'0 0 auto'} style={{ zIndex: 1 }}>
                    <SText p={6} color={'white'}>
                        <ConditionalRender if={!isSideSmallLogo}>
                            <SText
                                fontSize={4}
                                mb={4}
                                sx={{
                                    width: window?.ORGANIZATION.version === 2 ? '90%' : 'auto',
                                }}
                                fontWeight={'semiBold'}
                            >
                                {t('side_bar_bottom_header')}
                            </SText>
                            <SocialIcons>
                                {websiteLink && (
                                    <Link target="__blank" href={websiteLink} height={20}>
                                        <SIcon icon={WebsiteSvg} size={18} mr={3} tintColor="white" />
                                    </Link>
                                )}
                                {facebookLink && (
                                    <Link target="__blank" href={facebookLink} height={20}>
                                        <SIcon icon={FacebookSvg} size={18} mr={3} tintColor="white" />
                                    </Link>
                                )}
                                {twitterLink && (
                                    <Link target="__blank" href={twitterLink} height={20}>
                                        <SIcon icon={TwitterSvg} size={18} mr={3} tintColor="white" />
                                    </Link>
                                )}
                                {instagramLink && (
                                    <Link target="__blank" href={instagramLink} height={20}>
                                        <SIcon icon={InstagramSvg} size={18} mr={3} tintColor="white" />
                                    </Link>
                                )}
                            </SocialIcons>
                        </ConditionalRender>
                        <ConditionalRender if={isSideSmallLogo}>
                            <StyledF4GILogo
                                padding={25}
                                width="100%"
                                height="auto"
                                src={'/bottom_logos.svg'}
                                alt={'logo'}
                            />
                        </ConditionalRender>

                        <SText
                            fontSize={2}
                            mb={3}
                            sx={{
                                width: window?.ORGANIZATION.version === 2 ? '90%' : 'auto',
                                whiteSpace: 'pre-line',
                            }}
                            style={{ fontFamily: isSideSmallLogo ? theme.fonts.terms : theme.fonts.default }}
                        >
                            {t('side_bar_bottom_copyright')}
                        </SText>
                        <SText
                            fontSize={2}
                            mb={3}
                            sx={{
                                width: window?.ORGANIZATION.version === 2 ? '90%' : 'auto',
                            }}
                            style={{ fontFamily: isSideSmallLogo ? theme.fonts.terms : theme.fonts.default }}
                        >
                            {t('side_bar_bottom_f4gi_description')}
                        </SText>
                        {authenticatedUser && (
                            <SText
                                fontSize={2}
                                mt={3}
                                onClick={onClickSignOut}
                                sx={{ cursor: 'pointer' }}
                                id="sign-out-button"
                            >
                                {t('side_bar_button_sign_out')}
                            </SText>
                        )}
                    </SText>
                </SBox>
            </ConditionalRender>
        </Container>
    )
}

export default BrandedSideBarLegacy
